import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { getSafetyOverview, getUpcomingSafetyEvents } from "./safetyOverviewApi";

const DARK_PANEL = {
  border: "1px solid rgba(148, 163, 184, 0.3)",
  bgcolor: "rgba(4, 12, 28, 0.92)",
  color: "#f8fafc",
  boxShadow: "0 16px 34px rgba(0, 0, 0, 0.32)",
  backdropFilter: "blur(12px)",
};

const METRICS = [
  {
    key: "pending",
    label: "미평가",
    color: "#60a5fa",
    icon: AssignmentLateOutlinedIcon,
  },
  {
    key: "evaluated",
    label: "평가완료",
    color: "#22c55e",
    icon: FactCheckOutlinedIcon,
  },
  {
    key: "improving",
    label: "개선대기",
    color: "#f59e0b",
    icon: BuildCircleOutlinedIcon,
  },
  {
    key: "improved",
    label: "개선완료",
    color: "#34d399",
    icon: TaskAltOutlinedIcon,
  },
  {
    key: "highRisk",
    label: "고위험",
    color: "#ef4444",
    icon: WarningAmberRoundedIcon,
  },
];

const EVENT_META = {
  education: {
    label: "안전보건교육",
    shortLabel: "교육",
    color: "#60a5fa",
    bg: "rgba(37, 99, 235, 0.2)",
    icon: SchoolOutlinedIcon,
  },
  tbm: {
    label: "TBM 미실시",
    shortLabel: "TBM",
    color: "#fb7185",
    bg: "rgba(225, 29, 72, 0.2)",
    icon: GroupsOutlinedIcon,
  },
  inspection: {
    label: "안전점검 조치",
    shortLabel: "점검 조치",
    color: "#fb923c",
    bg: "rgba(234, 88, 12, 0.2)",
    icon: EngineeringOutlinedIcon,
  },
  msds: {
    label: "MSDS 만료",
    shortLabel: "MSDS",
    color: "#a78bfa",
    bg: "rgba(124, 58, 237, 0.2)",
    icon: ScienceOutlinedIcon,
  },
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatDate = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, "0"),
    String(value.getDate()).padStart(2, "0"),
  ].join("-");
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const formatKoreanDateTime = (value, allDay = false) => {
  if (!value) return "일정 미정";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  const dateText = `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}.${String(date.getDate()).padStart(2, "0")}`;
  if (allDay || !String(value).includes("T")) return dateText;
  return `${dateText} ${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes()
  ).padStart(2, "0")}`;
};

const uniqueCount = (rows, key) =>
  new Set(
    (Array.isArray(rows) ? rows : []).map((row, index) =>
      row?.[key] == null ? `row-${index}` : String(row[key])
    )
  ).size;

const isCompletedImprovement = (row) => toNumber(row?.improve_status) >= 20;

const getBeforeRisk = (row) => {
  if (row?.risk_score != null) return toNumber(row.risk_score);
  return toNumber(row?.frequency_level) * toNumber(row?.importancy_level);
};

const isHighRisk = (row) => {
  const completed = isCompletedImprovement(row);
  if (completed) return toNumber(row?.improve_after_risk) >= 16;
  return getBeforeRisk(row) >= 16;
};

const getEventSource = (event) => {
  const source = String(event?.source || event?.type || "").toLowerCase();
  if (source.includes("education")) return "education";
  if (source.includes("tbm")) return "tbm";
  if (source.includes("inspection")) return "inspection";
  if (source.includes("msds")) return "msds";
  return "inspection";
};

const getEventTitle = (event, source) => {
  if (source === "tbm") return `${event?.title || "대상 팀"} TBM 미실시`;
  if (source === "msds") return `${event?.title || "MSDS"} 만료 예정`;
  return event?.title || EVENT_META[source].label;
};

const normalizeUpcomingEvents = (events, today) => {
  const todayKey = formatDate(today);
  return (Array.isArray(events) ? events : [])
    .filter((event) => {
      const source = getEventSource(event);
      const dateKey = String(event?.start || "").slice(0, 10);
      if (!dateKey || dateKey < todayKey) return false;
      if (source === "tbm") return event.status === "missed";
      if (source === "education") return event.status !== "cancelled";
      if (source === "inspection") return !["done", "completed"].includes(event.status);
      return true;
    })
    .map((event) => {
      const source = getEventSource(event);
      return {
        ...event,
        source,
        meta: EVENT_META[source],
        displayTitle: getEventTitle(event, source),
      };
    })
    .sort((a, b) => String(a.start).localeCompare(String(b.start)));
};

const normalizeCalendarEvents = (events) =>
  (Array.isArray(events) ? events : [])
    .filter((event) => {
      const source = getEventSource(event);
      if (!event?.start) return false;
      if (source === "tbm") return event.status === "missed";
      if (source === "education") return event.status !== "cancelled";
      if (source === "inspection") {
        return !["done", "completed"].includes(event.status);
      }
      return true;
    })
    .map((event) => {
      const source = getEventSource(event);
      return {
        ...event,
        source,
        meta: EVENT_META[source],
        displayTitle: getEventTitle(event, source),
      };
    })
    .sort((a, b) => String(a.start).localeCompare(String(b.start)));

const getMonthGrid = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const lastDate = new Date(year, month + 1, 0).getDate();
  return [
    ...Array.from({ length: firstDay }, () => null),
    ...Array.from({ length: lastDate }, (_, index) => index + 1),
  ];
};

const detailRows = (event) => {
  if (!event) return [];
  const raw = event.raw || {};
  if (event.source === "education") {
    return [
      ["교육 유형", raw.edu_type],
      ["교육 대상", raw.target_desc],
      ["교육 장소", raw.location_name],
      ["교육 시간", raw.duration_min ? `${raw.duration_min}분` : null],
    ];
  }
  if (event.source === "tbm") {
    return [
      ["대상 팀", raw.team_name || event.title],
      ["상태", "미실시"],
      ["전체 대상", raw.total_members ? `${raw.total_members}명` : null],
    ];
  }
  if (event.source === "inspection") {
    return [
      ["조치 상태", raw.effective_sla_status || raw.sla_status || event.status],
      ["담당자", raw.assignee_name || raw.assignee_emp_name],
      ["조치 내용", raw.instruction],
      ["점검 내용", raw.inspection_content || raw.content],
    ];
  }
  return [
    ["제품명", raw.product_name || event.title],
    ["제조사", raw.manufacturer],
    ["주요 물질", raw.substance_name],
    ["CAS No.", raw.cas_no],
  ];
};

function MonthlyStatusPanel({ monthLabel, metrics, loading, hasError }) {
  const total = metrics.pending + metrics.evaluated;
  const completionRate = total ? Math.round((metrics.evaluated / total) * 100) : 0;

  return (
    <Paper
      elevation={0}
      data-testid="dt-monthly-risk-overview"
      sx={{
        ...DARK_PANEL,
        position: "absolute",
        top: 72,
        left: 102,
        width: 430,
        border: "1px solid rgba(217, 164, 82, 0.48)",
        bgcolor: "rgba(2, 8, 19, 0.91)",
        backgroundImage:
          "linear-gradient(135deg, rgba(15, 28, 48, 0.96), rgba(2, 8, 19, 0.92) 62%)",
        boxShadow: "0 18px 42px rgba(0, 0, 0, 0.42)",
        borderRadius: "6px",
        zIndex: 24,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.7, pt: 1.45, pb: 1.15 }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography
            sx={{
              fontSize: 14.5,
              fontWeight: 900,
              letterSpacing: 0,
              color: "#ffffff",
            }}
          >
            {monthLabel} 위험성평가 현황
          </Typography>
          <Tooltip title="이번 달 평가 및 개선 처리 기준">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
          </Tooltip>
        </Stack>
        {hasError && (
          <Chip
            label="일부 조회 지연"
            size="small"
            sx={{ height: 21, bgcolor: "rgba(245,158,11,.16)", color: "#fbbf24" }}
          />
        )}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          px: 0.95,
          pb: 1.2,
        }}
      >
        {METRICS.map((metric, index) => {
          return (
            <Box
              key={metric.key}
              sx={{
                minWidth: 0,
                px: 0.65,
                textAlign: "center",
                borderLeft: index ? "1px solid rgba(148,163,184,.28)" : 0,
              }}
            >
              <Typography
                noWrap
                sx={{ color: "#cbd5e1", fontSize: 10.5, fontWeight: 800 }}
              >
                {metric.label}
              </Typography>
              {loading ? (
                <Skeleton
                  width={28}
                  height={31}
                  sx={{ mx: "auto", bgcolor: "rgba(255,255,255,.1)" }}
                />
              ) : (
                <Typography
                  sx={{
                    mt: 0.45,
                    color: metric.color,
                    fontSize: 25,
                    lineHeight: 1.1,
                    fontWeight: 900,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {metrics[metric.key]}
                  <Box component="span" sx={{ ml: 0.25, color: "#94a3b8", fontSize: 10 }}>
                    건
                  </Box>
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1.1}
        sx={{ px: 1.7, pb: 1.45 }}
      >
        <Typography sx={{ color: "#cbd5e1", fontSize: 10.5, fontWeight: 700, flexShrink: 0 }}>
          평가 완료율 {completionRate}%
        </Typography>
        <LinearProgress
          variant="determinate"
          value={completionRate}
          sx={{
            flex: 1,
            height: 6,
            borderRadius: 4,
            bgcolor: "rgba(148,163,184,.18)",
            "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: "#22c55e" },
          }}
        />
      </Stack>
    </Paper>
  );
}

function UpcomingToast({ event, onOpen, onClose }) {
  const EventIcon = event.meta.icon;
  return (
    <Paper
      elevation={0}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(event)}
      onKeyDown={(keyEvent) => {
        if (keyEvent.key === "Enter" || keyEvent.key === " ") {
          keyEvent.preventDefault();
          onOpen(event);
        }
      }}
      sx={{
        ...DARK_PANEL,
        width: 314,
        borderRadius: "6px",
        borderLeft: `3px solid ${event.meta.color}`,
        cursor: "pointer",
        bgcolor: "rgba(3, 11, 25, 0.94)",
        transition:
          "transform 150ms ease, border-color 150ms ease, background-color 150ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          bgcolor: "rgba(10, 22, 43, 0.96)",
          borderColor: event.meta.color,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.1, py: 0.9 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "7px",
            bgcolor: event.meta.bg,
            color: event.meta.color,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <EventIcon sx={{ fontSize: 17 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.6}>
            <Typography sx={{ color: event.meta.color, fontSize: 10.5, fontWeight: 900 }}>
              {event.meta.shortLabel}
            </Typography>
            <Typography sx={{ color: "#94a3b8", fontSize: 10.5 }}>
              {formatKoreanDateTime(event.start, event.allDay)}
            </Typography>
          </Stack>
          <Typography
            noWrap
            sx={{ mt: 0.15, color: "#f8fafc", fontSize: 12.5, fontWeight: 800 }}
          >
            {event.displayTitle}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label="일정 알림 닫기"
          onClick={(clickEvent) => {
            clickEvent.stopPropagation();
            onClose(event.id);
          }}
          sx={{ color: "#94a3b8", p: 0.25, "&:hover": { color: "#fff" } }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <ChevronRightIcon sx={{ fontSize: 17, color: "#64748b" }} />
      </Stack>
    </Paper>
  );
}

function EmptyCalendarLauncher({ onOpen }) {
  return (
    <Tooltip title="안전상황 캘린더 열기" placement="right">
      <Paper
        elevation={0}
        role="button"
        tabIndex={0}
        aria-label="안전상황 캘린더 열기"
        onClick={onOpen}
        onKeyDown={(keyEvent) => {
          if (keyEvent.key === "Enter" || keyEvent.key === " ") {
            keyEvent.preventDefault();
            onOpen();
          }
        }}
        sx={{
          ...DARK_PANEL,
          width: 48,
          height: 48,
          display: "grid",
          placeItems: "center",
          borderRadius: "8px",
          color: "#dbeafe",
          bgcolor: "rgba(3, 11, 25, 0.94)",
          border: "1px solid rgba(96, 165, 250, 0.42)",
          cursor: "pointer",
          transition:
            "transform 150ms ease, border-color 150ms ease, background-color 150ms ease",
          "&:hover": {
            transform: "translateY(-2px)",
            color: "#ffffff",
            bgcolor: "rgba(14, 30, 56, 0.97)",
            borderColor: "#60a5fa",
          },
          "&:focus-visible": {
            outline: "2px solid #60a5fa",
            outlineOffset: 2,
          },
        }}
      >
        <CalendarMonthOutlinedIcon sx={{ fontSize: 27 }} />
      </Paper>
    </Tooltip>
  );
}

function SafetyCalendarDialog({
  open,
  monthDate,
  events,
  loading,
  onMonthChange,
  onEventOpen,
  onClose,
}) {
  const monthCells = useMemo(() => getMonthGrid(monthDate), [monthDate]);
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const monthEvents = useMemo(
    () =>
      events.filter((event) => {
        const eventDate = new Date(event.start);
        return eventDate.getFullYear() === year && eventDate.getMonth() === month;
      }),
    [events, month, year]
  );
  const eventsByDay = useMemo(() => {
    const map = new Map();
    monthEvents.forEach((event) => {
      const day = new Date(event.start).getDate();
      map.set(day, [...(map.get(day) || []), event]);
    });
    return map;
  }, [monthEvents]);

  return (
    <Dialog
      data-testid="dt-safety-calendar-dialog"
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "min(790px, calc(100vh - 56px))",
          borderRadius: "8px",
          color: "#f8fafc",
          bgcolor: "#06101f",
          backgroundImage:
            "linear-gradient(145deg, rgba(12, 27, 49, .98), rgba(3, 10, 22, .99))",
          border: "1px solid rgba(148, 163, 184, .28)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ px: 2.4, py: 1.65, borderBottom: "1px solid rgba(148,163,184,.2)" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.1}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "8px",
                display: "grid",
                placeItems: "center",
                color: "#93c5fd",
                bgcolor: "rgba(37, 99, 235, .2)",
              }}
            >
              <CalendarMonthOutlinedIcon />
            </Box>
            <Box>
              <Typography sx={{ color: "#ffffff", fontSize: 17, fontWeight: 900 }}>
                안전상황 캘린더
              </Typography>
              <Typography sx={{ mt: 0.1, fontSize: 11.5, color: "#94a3b8" }}>
                교육, TBM 미실시, 점검 조치와 MSDS 만료 일정을 확인합니다.
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} aria-label="안전상황 캘린더 닫기" sx={{ color: "#cbd5e1" }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Box sx={{ px: 2.4, pt: 1.45, pb: 1.1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <IconButton
              aria-label="이전 달"
              onClick={() => onMonthChange(new Date(year, month - 1, 1))}
              sx={{ color: "#e2e8f0", border: "1px solid rgba(148,163,184,.25)" }}
            >
              <ArrowBackIosNewRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography
              sx={{
                minWidth: 145,
                color: "#ffffff",
                textAlign: "center",
                fontSize: 18,
                fontWeight: 900,
              }}
            >
              {year}년 {month + 1}월
            </Typography>
            <IconButton
              aria-label="다음 달"
              onClick={() => onMonthChange(new Date(year, month + 1, 1))}
              sx={{ color: "#e2e8f0", border: "1px solid rgba(148,163,184,.25)" }}
            >
              <ArrowForwardIosRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={0.65}>
            {Object.values(EVENT_META).map((meta) => (
              <Chip
                key={meta.shortLabel}
                label={meta.shortLabel}
                size="small"
                sx={{ height: 24, bgcolor: meta.bg, color: meta.color, fontWeight: 900 }}
              />
            ))}
          </Stack>
        </Stack>
      </Box>

      {loading && (
        <LinearProgress
          sx={{
            height: 2,
            bgcolor: "rgba(96, 165, 250, .12)",
            "& .MuiLinearProgress-bar": { bgcolor: "#60a5fa" },
          }}
        />
      )}

      <DialogContent sx={{ px: 2.4, pt: 0, pb: 2.2, overflow: "hidden" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            mb: 0.55,
          }}
        >
          {["일", "월", "화", "수", "목", "금", "토"].map((weekday) => (
            <Typography
              key={weekday}
              sx={{ py: 0.45, textAlign: "center", color: "#94a3b8", fontSize: 11, fontWeight: 800 }}
            >
              {weekday}
            </Typography>
          ))}
        </Box>
        <Box
          sx={{
            height: "calc(100% - 28px)",
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            gridTemplateRows: `repeat(${Math.ceil(monthCells.length / 7)}, minmax(0, 1fr))`,
            gap: 0.55,
          }}
        >
          {monthCells.map((day, index) => {
            const dayEvents = day ? eventsByDay.get(day) || [] : [];
            return (
              <Box
                key={`${day || "blank"}-${index}`}
                sx={{
                  minWidth: 0,
                  minHeight: 0,
                  p: day ? 0.7 : 0,
                  borderRadius: "6px",
                  border: day ? "1px solid rgba(148,163,184,.2)" : 0,
                  bgcolor: day ? "rgba(15, 30, 52, .72)" : "transparent",
                  overflow: "hidden",
                }}
              >
                {day && (
                  <>
                    <Typography sx={{ color: "#e2e8f0", fontSize: 11.5, fontWeight: 900 }}>
                      {day}
                    </Typography>
                    <Stack
                      spacing={0.35}
                      sx={{
                        mt: 0.45,
                        pr: dayEvents.length > 3 ? 0.25 : 0,
                        maxHeight: "calc(100% - 20px)",
                        overflowY: "auto",
                        scrollbarWidth: "thin",
                        scrollbarColor: "rgba(148, 163, 184, .46) transparent",
                        "&::-webkit-scrollbar": { width: 5 },
                        "&::-webkit-scrollbar-track": {
                          bgcolor: "transparent",
                          borderRadius: 5,
                        },
                        "&::-webkit-scrollbar-thumb": {
                          bgcolor: "rgba(148, 163, 184, .38)",
                          borderRadius: 5,
                          border: "1px solid rgba(6, 16, 31, .7)",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          bgcolor: "rgba(147, 197, 253, .68)",
                        },
                      }}
                    >
                      {dayEvents.map((event) => {
                        const EventIcon = event.meta.icon;
                        return (
                          <Box
                            key={event.id}
                            data-testid="dt-safety-calendar-event"
                            role="button"
                            tabIndex={0}
                            onClick={() => onEventOpen(event)}
                            onKeyDown={(keyEvent) => {
                              if (keyEvent.key === "Enter" || keyEvent.key === " ") {
                                keyEvent.preventDefault();
                                onEventOpen(event);
                              }
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.45,
                              minWidth: 0,
                              px: 0.65,
                              py: 0.4,
                              borderRadius: "4px",
                              color: "#f8fafc",
                              bgcolor: event.meta.bg,
                              border: `1px solid ${event.meta.color}55`,
                              boxShadow: `inset 3px 0 0 ${event.meta.color}`,
                              cursor: "pointer",
                              "&:hover": {
                                bgcolor: event.meta.bg,
                                filter: "brightness(1.2)",
                              },
                            }}
                          >
                            <EventIcon
                              sx={{ color: event.meta.color, fontSize: 13, flexShrink: 0 }}
                            />
                            <Typography
                              noWrap
                              sx={{ color: "#f8fafc", fontSize: 10.5, fontWeight: 900 }}
                            >
                              {event.displayTitle}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function EventDetailDialog({ event, onClose }) {
  if (!event) return null;
  const EventIcon = event.meta.icon;
  const rows = detailRows(event).filter(([, value]) => value !== null && value !== undefined && value !== "");

  return (
    <Dialog
      data-testid="dt-safety-event-detail-dialog"
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "8px", overflow: "hidden" } }}
    >
      <DialogTitle sx={{ px: 2.25, py: 1.75 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1.15} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "8px",
                bgcolor: event.meta.bg,
                color: event.meta.color,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <EventIcon sx={{ fontSize: 21 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: "#0f172a", fontSize: 17, fontWeight: 900 }}>
                {event.displayTitle}
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                {formatKoreanDateTime(event.start, event.allDay)}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} aria-label="상세 닫기">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 2.25 }}>
        <Stack spacing={1.1}>
          <Chip
            label={event.meta.label}
            size="small"
            sx={{ alignSelf: "flex-start", bgcolor: event.meta.bg, color: event.meta.color, fontWeight: 900 }}
          />
          {rows.length ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "110px minmax(0, 1fr)",
                columnGap: 1.5,
                rowGap: 0,
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {rows.map(([label, value], index) => (
                <React.Fragment key={label}>
                  <Typography
                    sx={{
                      px: 1.35,
                      py: 1.15,
                      bgcolor: "#f8fafc",
                      color: "#64748b",
                      fontSize: 12,
                      fontWeight: 800,
                      borderTop: index ? "1px solid #e2e8f0" : 0,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={{
                      px: 1.35,
                      py: 1.15,
                      color: "#0f172a",
                      fontSize: 12.5,
                      fontWeight: 700,
                      borderTop: index ? "1px solid #e2e8f0" : 0,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {String(value)}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: "#64748b", fontSize: 13 }}>
              등록된 세부 정보가 없습니다.
            </Typography>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default function SafetyOverviewOverlay({ groupCode = "dev", projectCode = "ese" }) {
  const [metrics, setMetrics] = useState({
    pending: 0,
    evaluated: 0,
    improving: 0,
    improved: 0,
    highRisk: 0,
  });
  const [events, setEvents] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [loading, setLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const now = useMemo(() => new Date(), []);
  const monthLabel = `${now.getFullYear()}년 ${now.getMonth() + 1}월`;

  const load = useCallback(async () => {
    setLoading(true);
    const calendarEnd = addDays(now, 45);
    const [overviewResult, calendarResult] = await Promise.allSettled([
      getSafetyOverview({ groupCode, projectCode, date: now }),
      getUpcomingSafetyEvents({
        groupCode,
        projectCode,
        start: formatDate(now),
        end: formatDate(calendarEnd),
      }),
    ]);

    let failed = false;
    if (overviewResult.status === "fulfilled") {
      const data = overviewResult.value;
      const completedEvaluations = data.evaluations.filter(
        (row) => toNumber(row?.eval_status) >= 20
      );
      const latestByCause = new Map();
      data.evaluations.forEach((row, index) => {
        const key = row?.register_cause_id == null ? `row-${index}` : String(row.register_cause_id);
        const previous = latestByCause.get(key);
        if (!previous || toNumber(row.eval_id) > toNumber(previous.eval_id)) latestByCause.set(key, row);
      });
      setMetrics({
        pending: uniqueCount(data.pendingHazards, "register_cause_id"),
        evaluated: completedEvaluations.length,
        improving: data.improvementWaiting.filter((row) => !isCompletedImprovement(row)).length,
        improved: data.improvementCompleted.filter(isCompletedImprovement).length,
        highRisk: Array.from(latestByCause.values()).filter(isHighRisk).length,
      });
      failed = data.hasError;
    } else {
      failed = true;
    }

    if (calendarResult.status === "fulfilled") {
      setEvents(normalizeCalendarEvents(calendarResult.value?.events));
    } else {
      failed = true;
      setEvents([]);
    }

    setHasError(failed);
    setLoading(false);
  }, [groupCode, projectCode, now]);

  useEffect(() => {
    load();
    const interval = window.setInterval(load, 5 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [load]);

  const loadCalendarMonth = useCallback(
    async (monthDate) => {
      const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

      setCalendarLoading(true);
      try {
        const result = await getUpcomingSafetyEvents({
          groupCode,
          projectCode,
          start: formatDate(monthStart),
          end: formatDate(monthEnd),
        });
        setCalendarEvents(normalizeCalendarEvents(result?.events));
      } catch (error) {
        setCalendarEvents([]);
        setHasError(true);
      } finally {
        setCalendarLoading(false);
      }
    },
    [groupCode, projectCode]
  );

  const visibleEvents = normalizeUpcomingEvents(events, now)
    .filter((event) => !dismissedIds.has(String(event.id)))
    .slice(0, 3);

  const openCalendar = (event) => {
    const eventDate = new Date(event?.start || now);
    const targetMonth = new Date(eventDate.getFullYear(), eventDate.getMonth(), 1);
    setCalendarMonth(targetMonth);
    setCalendarOpen(true);
    loadCalendarMonth(targetMonth);
  };

  const changeCalendarMonth = (nextMonth) => {
    setCalendarMonth(nextMonth);
    loadCalendarMonth(nextMonth);
  };

  return (
    <>
      <MonthlyStatusPanel
        monthLabel={monthLabel}
        metrics={metrics}
        loading={loading}
        hasError={hasError}
      />

      {visibleEvents.length > 0 ? (
        <Stack
          data-testid="dt-upcoming-safety-events"
          spacing={0.8}
          sx={{ position: "absolute", left: 102, bottom: 22, zIndex: 26 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.65} sx={{ px: 0.2 }}>
            <CalendarMonthOutlinedIcon sx={{ color: "#cbd5e1", fontSize: 15 }} />
            <Typography sx={{ color: "#e2e8f0", fontSize: 11.5, fontWeight: 900 }}>
              다가오는 안전 일정
            </Typography>
          </Stack>
          {visibleEvents.map((event) => (
            <UpcomingToast
              key={event.id}
              event={event}
              onOpen={openCalendar}
              onClose={(id) =>
                setDismissedIds((previous) => {
                  const next = new Set(previous);
                  next.add(String(id));
                  return next;
                })
              }
            />
          ))}
        </Stack>
      ) : (
        <Box
          data-testid="dt-upcoming-safety-events"
          sx={{ position: "absolute", left: 102, bottom: 22, zIndex: 26 }}
        >
          <EmptyCalendarLauncher onOpen={() => openCalendar()} />
        </Box>
      )}

      <SafetyCalendarDialog
        open={calendarOpen}
        monthDate={calendarMonth}
        events={calendarEvents}
        loading={calendarLoading}
        onMonthChange={changeCalendarMonth}
        onEventOpen={setSelectedEvent}
        onClose={() => setCalendarOpen(false)}
      />

      <EventDetailDialog event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </>
  );
}
